import React, { useState } from 'react';
import * as wasm from 'sched';
import { Button } from 'flowbite-react';
import { useDatabase } from '../../hooks/useDatabase';
import { WorkspaceContext } from '../../context/WorkspaceContext';
import CreatePriorityForm from '../priorities/CreatePriorityForm';
import TypeBadge from '../common/TypeBadge';
import { FiArrowRight } from 'react-icons/fi';

export type ActorInput = { kind: 'Person' | 'Team'; id: string };

type Props =
  | { actor: ActorInput; actors?: never }
  | { actor?: never; actors: ActorInput[] };

const WorkMatchPairForActor: React.FC<Props> = ({ actor, actors }) => {
  const { database, applyCommand } = useDatabase();
  const { safeData } = React.useContext(WorkspaceContext);
  const [selectedObject, setSelectedObject] = useState<wasm.NodeMatcher | null>(null);
  const actorOptions: ActorInput[] = React.useMemo(() => (actors && actors.length > 0 ? actors : actor ? [actor] : []), [actor, actors]);
  const [selectedActor, setSelectedActor] = useState<ActorInput | null>(actorOptions[0] ?? null);
  const [submitting, setSubmitting] = useState(false);

  const actorLabel = (a: ActorInput) => {
    if (!database) return a.id;
    if (a.kind === 'Person') {
      const p = database.people()?.[a.id];
      return p ? p.name() : a.id;
    }
    const t = database.teams?.()?.[a.id];
    return t ? t.name() : a.id;
  };

  const onSubmit = async () => {
    if (!selectedObject || !selectedActor) return;
    setSubmitting(true);
    try {
      const pt: wasm.PersonOrTeam = selectedActor.kind === 'Person' ? { Person: selectedActor.id } : { Team: selectedActor.id };
      const command: wasm.DatabaseCommand = {
        type: 'SetWorkMatch',
        work_match: {
          actor: pt,
          object: selectedObject,
        },
        enabled: true,
      };
      await applyCommand(command);
      setSelectedObject(null);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-3 rounded-lg p-3 ring-1 ring-gray-200 bg-cm-bg-elevated/40/40">
      <div className="text-sm font-medium text-cm-text-primary mb-2">Add Match</div>
      <div className="grid grid-cols-[1fr_auto_1fr] gap-4 items-start overflow-hidden relative">
        {/* Left: search for What */}
        <div className="overflow-hidden">
          <div className="text-xs text-cm-text-muted mb-2">What</div>
          <button
            type="button"
            onClick={() => setSelectedObject('AllWork' as unknown as wasm.NodeMatcher)}
            className={`w-full mb-2 px-3 py-1.5 text-sm rounded-md border transition-colors ${
              selectedObject === ('AllWork' as unknown as wasm.NodeMatcher)
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 ring-2 ring-blue-500'
                : 'border-cm-border-subtle text-cm-text-secondary hover:bg-cm-bg-hover'
            }`}
          >
            All work
          </button>
          <CreatePriorityForm
            compact
            onCancel={() => void 0}
            onSubmit={(n) => setSelectedObject(n)}
            owner={{ type_: selectedActor?.kind ?? 'Person', id: selectedActor?.id ?? '' }}
          />
          {selectedObject && (
            <div className="mt-2 text-xs text-cm-text-secondary">Selected. Click <span className="font-medium">Set match</span> to create the link, or choose another item.</div>
          )}
        </div>

        {/* Pairing visual */}
        <div className="relative flex items-center justify-center h-full select-none">
          <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-px bg-cm-border-subtle" />
          <div className="relative z-10 inline-flex items-center justify-center rounded-full px-2 py-1 text-cm-text-muted bg-cm-bg-elevated ring-1 ring-gray-200">
            <FiArrowRight />
          </div>
        </div>

        {/* Right: selectable Actor(s) */}
        <div className="overflow-hidden">
          <div className="text-xs text-cm-text-muted mb-2">Who</div>
          <div className="space-y-1 max-h-64 overflow-auto pr-1">
            {actorOptions.map((a) => {
              const isSelected = !!selectedActor && selectedActor.kind === a.kind && selectedActor.id === a.id;
              return (
                <Button
                  key={`${a.kind}:${a.id}`}
                  color="light"
                  size="xs"
                  onClick={() => setSelectedActor(isSelected ? null : a)}
                  className={`w-full justify-start gap-2 rounded-md !border !border-cm-border-subtle hover:!bg-cm-bg-hover ${isSelected ? '!ring-2 !ring-blue-500 !bg-blue-50' : ''}`}
                >
                  <TypeBadge t={a.kind} />
                  <span className="truncate flex-1">{actorLabel(a)}</span>
                </Button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="mt-4">
        <Button color="blue" size="md" onClick={onSubmit} disabled={!selectedObject || !selectedActor || submitting} className="w-full font-semibold">
          Set match
        </Button>
      </div>
    </div>
  );
};

export default WorkMatchPairForActor;
